import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtMoney2, fmtPct, GRADE_BADGE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Stamp, ShieldAlert } from "lucide-react";

export default function InvestorApplicationDetails() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);

  const [maxAmount, setMaxAmount] = useState(15000);
  const [maxApr, setMaxApr] = useState(20);
  const [maxTerm, setMaxTerm] = useState(48);
  const [freq, setFreq] = useState("monthly");
  const [signedName, setSignedName] = useState("");
  const [agree, setAgree] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/applications/${id}`)
      .then((r) => {
        setApp(r.data);
        const bud = r.data.vehicle_preferences?.max_budget;
        if (bud) setMaxAmount(bud);
      })
      .catch(() => navigate("/applications"));
  }, [id, navigate]);

  useEffect(() => {
    if (user?.name) setSignedName(user.name);
  }, [user]);

  if (!app) return <div className="px-4 py-16 text-muted-foreground">Loading…</div>;

  const p = app.derived_profile || {};
  const v = app.vehicle_preferences || {};
  const depositRequired = app.deposit_amount ?? 2500;

  const submit = async () => {
    if (user?.role !== "user" && user?.role !== "dealer") { toast.error("Investor or dealer accounts only"); return; }
    if (!agree) { toast.error("You must agree to the contract"); return; }
    if (!signedName.trim()) { toast.error("Type your name to sign"); return; }
    if (maxApr <= 0 || maxApr > 28) { toast.error("APR must be 0 < x ≤ 28"); return; }
    setBusy(true);
    try {
      await api.post(`/applications/${id}/preapprovals`, {
        max_amount: Number(maxAmount), max_apr: Number(maxApr),
        max_term: Number(maxTerm), payment_frequency: freq,
        signed_name: signedName, agree: true, message,
      });
      toast.success("Pre-approval submitted — deposit pending admin verification");
      navigate("/applications");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-application-details" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/applications" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Applications
      </Link>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10">
        <div className="space-y-8">
          <div>
            <div className="font-overline text-accent">Anonymized Application</div>
            <h1 className="font-display text-4xl mt-2">{(v.body_type || "Vehicle").toUpperCase()} · up to {fmtMoney(v.max_budget)}</h1>
            <div className="mt-3 flex gap-2">
              <span className={`px-2 py-1 text-[10px] font-mono-data uppercase tracking-wider ${GRADE_BADGE[p.credit_tier] || ""}`}>Tier {p.credit_tier || "?"}</span>
              {app.credit_score_band && <span className="px-2 py-1 text-[10px] font-mono-data uppercase tracking-wider border border-border">Score {app.credit_score_band}</span>}
            </div>
          </div>

          <div className="border border-border divide-y divide-border">
            <Row label="Credit Tier" value={`Tier ${p.credit_tier || "?"}`} />
            <Row label="Score Band" value={app.credit_score_band || "—"} />
            <Row label="Monthly Income" value={p.monthly_income_range || "—"} />
            <Row label="DTI" value={p.dti_ratio ? `${p.dti_ratio}%` : "—"} />
            <Row label="Employment" value={(p.employment_type || "—").replace("_", " ")} />
            <Row label="Time on Job" value={p.time_on_job_months ? `${p.time_on_job_months} mo` : "—"} />
            <Row label="Prior Repos" value={p.prior_repos ?? "—"} />
            <Row label="State" value={p.state || "—"} />
          </div>

          <div>
            <div className="font-overline mb-3">Vehicle Preferences</div>
            <div className="border border-border divide-y divide-border">
              <Row label="Max Budget" value={fmtMoney(v.max_budget)} highlight />
              <Row label="Body Type" value={v.body_type || "—"} />
              <Row label="Year Range" value={`${v.year_min || "—"}–${v.year_max || "—"}`} />
              <Row label="Max Mileage" value={v.max_mileage ? `${v.max_mileage.toLocaleString()} mi` : "—"} />
              <Row label="Color" value={v.color_preference || "—"} />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="border border-border p-6 space-y-5">
            <div>
              <div className="font-overline text-accent">Submit Pre-Approval</div>
              <h2 className="font-display text-2xl mt-1">Funding Commitment</h2>
            </div>

            <div>
              <Label className="font-overline mb-2 block">Max Amount Willing to Fund</Label>
              <div className="flex items-center gap-2">
                <span className="font-mono-data text-muted-foreground">$</span>
                <Input type="number" value={maxAmount} onChange={(e) => setMaxAmount(Number(e.target.value) || 0)}
                  className="rounded-none font-mono-data text-lg" data-testid="pa-max-amount" />
              </div>
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-2">
                <Label className="font-overline">Max APR</Label>
                <span className="font-mono-data text-primary text-2xl" data-testid="pa-rate-display">{fmtPct(maxApr)}</span>
              </div>
              <Slider min={1} max={28} step={0.25} value={[maxApr]} onValueChange={([v2]) => setMaxApr(v2)} data-testid="pa-rate-slider" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-overline mb-2 block">Payment Frequency</Label>
                <Select value={freq} onValueChange={setFreq}>
                  <SelectTrigger className="rounded-none" data-testid="pa-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-overline mb-2 block">Max Term (# pmts)</Label>
                <Input type="number" value={maxTerm} onChange={(e) => setMaxTerm(Math.max(1, Number(e.target.value) || 1))}
                  className="rounded-none font-mono-data" data-testid="pa-term" />
              </div>
            </div>

            <div className="border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm" data-testid="pa-deposit-banner">
              <div className="flex items-start gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-700 dark:text-amber-300 flex-shrink-0 mt-0.5" />
                <div className="text-amber-900 dark:text-amber-100">
                  <div className="font-semibold">$2,500 Escrow Deposit Required</div>
                  <div className="font-mono-data text-2xl mt-1" data-testid="pa-deposit-amount">{fmtMoney2(depositRequired)}</div>
                  <div className="text-xs mt-2">
                    Held in escrow. Refunded if your pre-approval expires unmatched or is cancelled, and returned to you at funding.
                    Forfeited only if you miss the 2-business-day funding deadline after agreeing on a vehicle.
                    Forfeiture split: <b>40% Dealer · 40% FinanceLocal · 20% Applicant</b>.
                  </div>
                </div>
              </div>
            </div>

            <details className="border border-border" data-testid="pa-contract">
              <summary className="px-4 py-3 cursor-pointer font-overline text-sm">Funding Commitment Agreement ({app.contract_version})</summary>
              <pre className="px-4 pb-4 text-[11px] text-muted-foreground whitespace-pre-wrap leading-relaxed font-mono-data">{app.contract_text}</pre>
            </details>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="rounded-none mt-1" data-testid="pa-agree" />
                <div className="text-sm">I have read and agree to the Funding Commitment Agreement, including the $2,500 escrow deposit and 2-business-day funding deadline.</div>
              </div>
              <div>
                <Label className="font-overline mb-2 block">Sign by typing your full legal name</Label>
                <Input value={signedName} onChange={(e) => setSignedName(e.target.value)}
                  className="rounded-none font-display text-lg italic" data-testid="pa-signature" />
              </div>
              <div>
                <Label className="font-overline mb-2 block">Note to admin (optional)</Label>
                <Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-none" />
              </div>
            </div>

            <Button onClick={submit} disabled={busy || !agree} className="w-full rounded-none uppercase tracking-wider h-12" data-testid="pa-submit">
              <Stamp className="w-4 h-4 mr-2" />{busy ? "Submitting…" : "Submit Pre-Approval"}
            </Button>
            <p className="text-[11px] text-muted-foreground">Pre-approval is binding once admin confirms deposit receipt. Valid 30 days.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <div className="font-overline text-muted-foreground">{label}</div>
      <div className={`font-mono-data ${highlight ? "text-primary text-lg" : ""}`}>{value}</div>
    </div>
  );
}
